import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBan, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * Anulação de protocolo.
 *
 * Deixa explícito que o número não volta: no livro de papel a linha era
 * riscada e o número seguinte continuava de onde estava, e é esse
 * comportamento que o cliente pediu para manter.
 */
const AnularProtocoloModal = ({ protocolo, onConfirmar, onFechar }) => {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  const confirmar = async (e) => {
    e.preventDefault();
    if (enviando) return;

    if (motivo.trim().length < 5) {
      setErro('Descreva o motivo da anulação (ao menos 5 caracteres).');
      return;
    }

    setErro('');
    setEnviando(true);

    try {
      await onConfirmar(motivo.trim());
    } catch (err) {
      setErro(err.fieldErrors?.motivo?.[0] || err.message || 'Não foi possível anular o protocolo.');
      setEnviando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-anular-protocolo"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 id="titulo-anular-protocolo" className="text-lg font-semibold text-gray-800">
            Anular protocolo {protocolo.numero}
          </h3>
          <button
            type="button"
            onClick={onFechar}
            className="p-2 rounded text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label="Fechar"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={confirmar} className="px-4 py-4">
          <dl className="mb-4 text-sm text-gray-700 space-y-1">
            <div className="flex gap-2">
              <dt className="font-medium">Requerente:</dt>
              <dd>{protocolo.requerente_nome}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium">Extensão:</dt>
              <dd>{protocolo.extensaoNome || '-'}</dd>
            </div>
          </dl>

          <div className="mb-4 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            O número {protocolo.numero} continuará registrado e <strong>não será reaproveitado</strong>.
            A anulação não pode ser desfeita.
          </div>

          <label htmlFor="motivo-anulacao" className="block text-sm text-gray-700 mb-1">
            Motivo da anulação <span className="text-red-600">*</span>
          </label>
          <textarea
            id="motivo-anulacao"
            rows={3}
            maxLength={2000}
            value={motivo}
            onChange={(e) => { setMotivo(e.target.value); setErro(''); }}
            className={`w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 ${
              erro ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-green-400'
            }`}
            placeholder="Ex.: requerimento entregue em duplicidade"
          />
          {erro && <p className="mt-1 text-sm text-red-600">{erro}</p>}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={enviando}
              className="min-h-11 px-4 py-2 rounded bg-red-600 text-white font-semibold shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={enviando ? faSpinner : faBan} spin={enviando} />
              <span>{enviando ? 'Anulando...' : 'Anular protocolo'}</span>
            </button>
            <button
              type="button"
              onClick={onFechar}
              className="min-h-11 px-4 py-2 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnularProtocoloModal;
